import { Component, OnInit, computed, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Eventsetup } from '../../eventsetup';
import { EventsetupStoreService } from '../../eventsetup-store.service';
import { environment } from '../../../../environments/environment';
import { EventconfigStoreService } from 'app/shared/eventconfig-store.service';
import { MediaLibraryEvent, MediaLibraryStoreService } from '../../media-library-store.service';

@Component({
  selector: 'lse-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: false
})
export class HeaderComponent implements OnInit {
  eLang: string[] = [];
  activeLang!: string;
  screenwidth!: number;
  uiAssetsPrefix = '';
  selectedLanguage!: string;
  fullLanguage: any[] = [];

  /** Token des laufenden Events — markiert den aktiven Eintrag im Event-Menü. */
  eventToken = '';

  eSetup!: Eventsetup;
  down = false;

  readonly allLanguages = [
    { code: 'de', img: 'ger', name: 'Deutsch' },
    { code: 'en', img: 'uk',  name: 'English' },
    { code: 'nl', img: 'nl',  name: 'Nederlands' },
    { code: 'pt', img: 'pt',  name: 'Português' },
    { code: 'es', img: 'es',  name: 'Español' },
    { code: 'cn', img: 'cn',  name: '汉语' },
    { code: 'jp', img: 'jp',  name: '日本語' },
  ];

  get langPosition(): 'left' | 'middle' | 'right' {
    if (!this.eSetup) return 'right';
    const { mainLogoPosition, secondLogoPosition } = this.eSetup;
    if (secondLogoPosition === 'none') {
      return mainLogoPosition === 'right' ? 'left' : 'right';
    }
    const positions = ['left', 'middle', 'right'] as const;
    return positions.find(p => p !== mainLogoPosition && p !== secondLogoPosition) ?? 'right';
  }

  constructor(private eventSetupService: EventsetupStoreService) {}

  private eventConfigService = inject(EventconfigStoreService);
  private activatedRoute = inject(ActivatedRoute);
  readonly mediaLib = inject(MediaLibraryStoreService);

  readonly eLangGlob = computed(() => this.eventConfigService.getLangGlobals());

  /**
   * Der Rücksprung-Button gehört in den Header, das Event-Menü braucht zusätzlich
   * Events — ohne sie wäre der Hamburger ein Schalter für eine leere Liste.
   */
  readonly showMediaLibraryLink = computed(() => this.mediaLib.isFromMediaLibrary());
  readonly showEventMenu = computed(() =>
    this.mediaLib.isFromMediaLibrary() && this.mediaLib.events().length > 0
  );

  ngOnInit() {
    this.uiAssetsPrefix = environment.imgAssets;
    this.screenwidth = window.innerWidth;
    this.activeLang = new URLSearchParams(window.location.search).get('ln') ?? '';
    this.eventToken = this.activatedRoute.snapshot.paramMap.get('token') ?? '';

    this.eventSetupService.setup$.subscribe((setup) => {
      this.eSetup = setup;
      this.eLang = setup.lang;
      if (!this.activeLang) {
        this.activeLang = setup.lang[0] ?? '';
      }
      this.fullLanguage = this.allLanguages
        .filter(l => this.eLang.includes(l.code) && l.code !== this.activeLang)
        .map(l => ({ value: l.code, name: l.name }));
      this.selectedLanguage = this.allLanguages.find(l => l.code === this.activeLang)?.name ?? '';
    });
  }


  switchLang(lang: string) {
    if (this.activeLang !== lang) {
      this.eventConfigService.setActiveLang(lang);
      const url = window.location.href.split('?')[0];
      if (this.mediaLib.isFromMediaLibrary()) {
        // Token und Rücksprung-Link müssen mit: ohne sie verlangt die Seite nach dem
        // Neuladen wieder einen Login.
        window.location.href =
          url + '?ln=' + lang +
          '&medialib=' + this.mediaLib.mToken() +
          '&back=' + this.mediaLib.backLinkEncoded();
      } else {
        window.location.href = url + '?ln=' + lang;
      }
    }
  }

  toggleDown(closeHam: boolean) {
    this.down = !this.down;
    if (this.down && closeHam) {
      this.closeBurger();
    }
  }

  closeDown() {
    this.down = false;
  }

  // ── Mediathek-Menü ─────────────────────────────────────────────

  /**
   * Titel des laufenden Events als Überschrift im Menü. Auf der Login-Seite ist die
   * Config noch nicht geladen — dann bleibt die Überschrift weg.
   */
  eventHeadline(): string {
    return this.eventConfigService.getStoredConfig()?.title ?? '';
  }

  /** Link zurück ins Dashboard der Mediathek, in der aktiven Sprache. */
  backLinkWithLang(): string {
    return this.mediaLib.backLink() + '?ln=' + this.activeLang;
  }

  eventsForLang(): MediaLibraryEvent[] {
    return this.mediaLib.eventsForLang(this.activeLang);
  }

  eventTitle(event: MediaLibraryEvent): string {
    return this.mediaLib.titleFor(event, this.activeLang);
  }

  eventUrl(event: MediaLibraryEvent): string {
    return this.mediaLib.eventUrl(event.token, this.activeLang);
  }

  toggleBurger() {
    this.mediaLib.togglePanel();
    this.down = false;
    this.lockBodyScroll(this.mediaLib.panelOpen());
  }

  closeBurger() {
    this.mediaLib.closePanel();
    this.lockBodyScroll(false);
  }

  /**
   * Das Menü liegt als fixe Ebene über der Seite. Ohne die Sperre scrollt darunter der
   * Seiteninhalt weiter, während im Menü gescrollt wird.
   */
  private lockBodyScroll(lock: boolean): void {
    document.body.style.overflow = lock ? 'hidden' : 'auto';
    document.body.style.height = lock ? '100vh' : 'auto';
  }
}
